import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import api from "../api";

const Landing = () => {
  const { t } = useI18n();
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/2294361/pexels-photo-2294361.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920')",
            backgroundSize: "cover",
            backgroundPosition: "center 25%",
            opacity: 0.65,
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.55) 50%, rgba(10,10,10,0.2) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
          <div className="font-mono text-xs tracking-[0.3em] text-[#FF3B30] mb-6" data-testid="landing-eyebrow">
            // {t.landing.eyebrow}
          </div>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
            {t.landing.h1_a} <br />
            <span style={{ color: "#FF3B30" }}>{t.landing.h1_b}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            {t.landing.sub}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/pricing" data-testid="landing-cta-start">
              <button className="btn-primary">{t.landing.cta_start} →</button>
            </Link>
            <Link to="/demo" data-testid="landing-cta-demo">
              <button className="btn-secondary" style={{ borderColor: "#FF3B3070", color: "#FF6B61" }}>
                ▶ {t.landing.cta_demo}
              </button>
            </Link>
            <Link to="/login" data-testid="landing-cta-haveCode">
              <button className="btn-secondary">{t.landing.cta_have_code}</button>
            </Link>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: t.landing.f1_title, b: t.landing.f1_body, idx: "01" },
            { t: t.landing.f2_title, b: t.landing.f2_body, idx: "02" },
            { t: t.landing.f3_title, b: t.landing.f3_body, idx: "03" },
          ].map((f, i) => (
            <div key={i} className="sne-card animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`landing-feature-${i}`}>
              <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-3">{f.idx}</div>
              <h3 className="font-heading text-2xl mb-2">{f.t}</h3>
              <p className="text-white/70">{f.b}</p>
            </div>
          ))}
        </div>

        {/* Humanitarian Bonus */}
        <div
          className="mt-6 sne-card relative overflow-hidden"
          style={{ borderColor: "#FF3B30", background: "#181111" }}
          data-testid="humanitarian-bonus"
        >
          <div className="flex flex-col md:flex-row items-start gap-4">
            <div
              className="font-mono text-[10px] tracking-widest px-3 py-1 whitespace-nowrap"
              style={{ background: "#FF3B30", color: "white" }}
            >
              💚 {t.landing.humanitarian_badge}
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-xl mb-1">{t.landing.humanitarian_title}</h3>
              <p className="text-sm text-white/70">{t.landing.humanitarian_body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Athletes — humanized story section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" data-testid="real-athletes-section">
        <div className="font-mono text-xs tracking-[0.3em] text-[#FF3B30] mb-4">{t.landing.real_eyebrow}</div>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-8 max-w-4xl leading-tight">
          {t.landing.real_title}
        </h2>
        <div className="space-y-5 max-w-3xl text-white/80 text-lg leading-relaxed">
          <p>{t.landing.real_intro_p1}</p>
          <p>{t.landing.real_intro_p2}</p>
          <p>{t.landing.real_intro_p3}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-12">
          {[
            { t: t.landing.real_feat_1_title, b: t.landing.real_feat_1_body },
            { t: t.landing.real_feat_2_title, b: t.landing.real_feat_2_body },
            { t: t.landing.real_feat_3_title, b: t.landing.real_feat_3_body },
            { t: t.landing.real_feat_4_title, b: t.landing.real_feat_4_body },
          ].map((f, i) => (
            <div key={i} className="p-6" style={{ background: "#0E0E10", borderLeft: "3px solid #FF3B30" }} data-testid={`real-feat-${i}`}>
              <h3 className="font-heading text-2xl mb-2 text-white">{f.t}</h3>
              <p className="text-white/70 text-base leading-relaxed">{f.b}</p>
            </div>
          ))}
        </div>

        {/* Differentiator */}
        <div className="mt-16 pt-10" style={{ borderTop: "1px solid #27272A" }}>
          <h3 className="font-heading text-3xl md:text-4xl mb-6">{t.landing.diff_title}</h3>
          <p className="text-white/70 mb-2">{t.landing.diff_q}</p>
          <p className="font-heading text-xl md:text-2xl text-white mb-8 max-w-3xl leading-snug">
            {t.landing.diff_q_emph}
          </p>
          <div className="space-y-2 mb-10">
            <p className="text-white/90 text-lg">{t.landing.diff_l1}</p>
            <p className="text-white/90 text-lg">{t.landing.diff_l2}</p>
            <p className="text-white/90 text-lg">{t.landing.diff_l3}</p>
          </div>
          <div className="mt-8 pt-6" style={{ borderTop: "1px solid #1F1F1F" }}>
            <p className="text-white/70 text-lg mb-2">{t.landing.diff_closing_a}</p>
            <p className="font-heading text-2xl md:text-3xl" style={{ color: "#FF3B30" }}>
              {t.landing.diff_closing_b}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="font-mono text-xs tracking-widest text-white/50 mb-3">// 02 — PRICING</div>
        <h2 className="font-heading text-4xl md:text-5xl mb-3">{t.landing.pricing_title}</h2>
        <p className="text-white/70 mb-10 max-w-xl">{t.landing.pricing_sub}</p>
        <div className="grid md:grid-cols-2 gap-6">
          <PricingCard pkg="plan_4mo" amount={50} label={t.landing.pkg_4mo} />
          <PricingCard pkg="plan_1y" amount={100} label={t.landing.pkg_1y} highlight />
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

const Newsletter = () => {
  const { t } = useI18n();
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="sne-card relative overflow-hidden" style={{ borderColor: "#FF3B30" }}>
        <div
          className="absolute -top-20 -right-20 opacity-20 pointer-events-none"
          style={{
            width: 320, height: 320,
            background: "radial-gradient(circle, #FF3B30 0%, transparent 70%)",
          }}
        />
        <div className="relative">
          <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-3">{t.landing.newsletter_eyebrow}</div>
          <h2 className="font-heading text-3xl md:text-4xl mb-3 max-w-2xl">{t.landing.newsletter_title}</h2>
          <p className="text-white/70 max-w-xl">{t.landing.newsletter_sub}</p>
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ pkg, amount, label, highlight }) => {
  const { t, lang } = useI18n();
  const features = lang === "ro"
    ? [
        "Plan zilnic + săptămânal complet",
        "Suplimente & vitamine",
        "Sistem pofte (rețete românești)",
        "Export PDF (planul final)",
        "Cod acces multi-device",
      ]
    : [
        "Full daily + weekly plans",
        "Supplements & vitamins",
        "Romanian cravings system",
        "PDF export (final plan)",
        "Multi-device access code",
      ];
  return (
    <div
      className="sne-card flex flex-col gap-4 relative"
      style={highlight ? { borderColor: "#FF3B30" } : {}}
      data-testid={`pricing-card-${pkg}`}
    >
      {highlight && (
        <span className="absolute top-4 right-4 font-mono text-[10px] tracking-widest text-white px-2 py-1" style={{ background: "#FF3B30" }}>
          {t.landing.pkg_best}
        </span>
      )}
      <div className="font-mono text-xs tracking-widest text-white/50">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-6xl">€{amount}</span>
      </div>
      <ul className="text-sm text-white/70 space-y-2">
        {features.map((f, i) => (
          <li key={i}>✓ {f}</li>
        ))}
      </ul>
      <Link to={`/pricing?pkg=${pkg}`} data-testid={`pricing-buy-${pkg}`} className="mt-auto">
        <button className="btn-primary w-full">{t.landing.pkg_buy} →</button>
      </Link>
    </div>
  );
};

export default Landing;
